import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { removeUndefinedDeep } from './removeUndefinedDeep';
import type {
  HandbookTrainingFreshnessReport,
  HandbookTrainingFreshnessStatus,
  HandbookTrainingNode,
  HandbookTrainingNodeFirestore,
  HandbookTrainingNodeKind,
} from '../types/handbookTraining';
import { parseLoomVideoId } from './loomEmbed';
import { isValidHandbookTrainingSlug, normalizeHandbookTrainingSlug } from './handbookTrainingAccess';
import {
  computeSiblingOrderAfterInsert,
  validateHandbookTrainingMove as assertValidHandbookTrainingMove,
} from './handbookTrainingTree';

export const HANDBOOK_TRAINING_NODES = 'handbookTrainingNodes';
export const HANDBOOK_TRAINING_FRESHNESS = 'handbookTrainingFreshnessReports';

function toDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function mapNode(id: string, d: HandbookTrainingNodeFirestore): HandbookTrainingNode {
  return {
    id,
    kind: d.kind === 'video' ? 'video' : 'folder',
    parentId: d.parentId === undefined || d.parentId === '' ? null : String(d.parentId),
    title: String(d.title || ''),
    description: typeof d.description === 'string' ? d.description : undefined,
    loomUrl: typeof d.loomUrl === 'string' ? d.loomUrl : undefined,
    sortOrder: typeof d.sortOrder === 'number' ? d.sortOrder : 0,
    published: d.published === true,
    slug: typeof d.slug === 'string' ? d.slug : undefined,
    recordedAt: typeof d.recordedAt === 'string' ? d.recordedAt : undefined,
    durationSec: typeof d.durationSec === 'number' ? d.durationSec : undefined,
    covers: d.covers,
    outline: Array.isArray(d.outline) ? d.outline : undefined,
    staleIf: Array.isArray(d.staleIf) ? d.staleIf.map(String) : undefined,
    lastAgentReviewAt: toDate(d.lastAgentReviewAt),
    updatedByUserId: typeof d.updatedByUserId === 'string' ? d.updatedByUserId : undefined,
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

export function subscribeHandbookTrainingNodes(
  onData: (nodes: HandbookTrainingNode[]) => void,
  onError?: (e: Error) => void,
  options?: { forEditors?: boolean },
): Unsubscribe {
  const col = collection(db, HANDBOOK_TRAINING_NODES);
  // Rules tie read access to `published`; viewers must query published docs only or the listener fails.
  const q =
    options?.forEditors === false ? query(col, where('published', '==', true)) : col;
  return onSnapshot(
    q,
    snap => {
      const rows: HandbookTrainingNode[] = [];
      snap.forEach(s => rows.push(mapNode(s.id, s.data() as HandbookTrainingNodeFirestore)));
      rows.sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
      onData(rows);
    },
    err => onError?.(err as Error),
  );
}

export function subscribeHandbookTrainingFreshnessReports(
  onData: (rows: HandbookTrainingFreshnessReport[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const col = collection(db, HANDBOOK_TRAINING_FRESHNESS);
  return onSnapshot(
    col,
    snap => {
      const rows: HandbookTrainingFreshnessReport[] = [];
      snap.forEach(s => {
        const d = s.data();
        rows.push({
          id: s.id,
          videoSlug: String(d.videoSlug || ''),
          catalogVersion: String(d.catalogVersion || ''),
          catalogHash: String(d.catalogHash || ''),
          status: (d.status as HandbookTrainingFreshnessStatus) || 'open',
          findings: String(d.findings || ''),
          suggestedUpdates:
            d.suggestedUpdates && typeof d.suggestedUpdates === 'object'
              ? (d.suggestedUpdates as Record<string, unknown>)
              : undefined,
          agentId: String(d.agentId || ''),
          createdAt: toDate(d.createdAt) ?? new Date(),
          updatedAt: toDate(d.updatedAt),
        });
      });
      rows.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      onData(rows);
    },
    err => onError?.(err as Error),
  );
}

export async function slugTakenByOtherVideo(slug: string, excludeNodeId?: string): Promise<boolean> {
  const q = query(collection(db, HANDBOOK_TRAINING_NODES), where('slug', '==', slug));
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    if (excludeNodeId && d.id === excludeNodeId) continue;
    if (d.data().kind === 'video') return true;
  }
  return false;
}

export type SaveHandbookTrainingNodeInput = {
  kind: HandbookTrainingNodeKind;
  parentId: string | null;
  title: string;
  description?: string;
  loomUrl?: string;
  published?: boolean;
  slug?: string;
  recordedAt?: string;
  durationSec?: number;
  covers?: HandbookTrainingNode['covers'];
  outline?: HandbookTrainingNode['outline'];
  staleIf?: string[];
  updatedByUserId: string;
};

export async function validateHandbookTrainingSave(
  input: SaveHandbookTrainingNodeInput,
  existingId?: string,
): Promise<string | null> {
  const title = input.title.trim();
  if (!title) return 'Title is required.';
  if (input.kind === 'video') {
    const url = (input.loomUrl || '').trim();
    if (!url) return 'Loom URL is required for videos.';
    if (!parseLoomVideoId(url)) return 'Paste a valid Loom share or embed link.';
    const slug = normalizeHandbookTrainingSlug(input.slug || title);
    if (!isValidHandbookTrainingSlug(slug)) return 'Slug must be lowercase letters, numbers, and hyphens.';
    if (await slugTakenByOtherVideo(slug, existingId)) return `Slug "${slug}" is already used by another video.`;
  }
  return null;
}

export async function createHandbookTrainingNode(
  input: SaveHandbookTrainingNodeInput,
  sortOrder: number,
): Promise<string> {
  const err = await validateHandbookTrainingSave(input);
  if (err) throw new Error(err);

  const payload: HandbookTrainingNodeFirestore = {
    kind: input.kind,
    parentId: input.parentId,
    title: input.title.trim(),
    sortOrder,
    published: input.published === true,
    updatedByUserId: input.updatedByUserId,
    createdAt: serverTimestamp() as unknown as HandbookTrainingNodeFirestore['createdAt'],
    updatedAt: serverTimestamp() as unknown as HandbookTrainingNodeFirestore['updatedAt'],
  };
  if (input.description?.trim()) payload.description = input.description.trim();
  if (input.kind === 'video') {
    payload.loomUrl = input.loomUrl!.trim();
    payload.slug = normalizeHandbookTrainingSlug(input.slug || input.title);
    if (input.recordedAt?.trim()) payload.recordedAt = input.recordedAt.trim();
    if (input.durationSec != null && input.durationSec > 0) payload.durationSec = input.durationSec;
    if (input.covers) payload.covers = input.covers;
    if (input.outline?.length) payload.outline = input.outline;
    if (input.staleIf?.length) payload.staleIf = input.staleIf;
  }

  const ref = await addDoc(collection(db, HANDBOOK_TRAINING_NODES), removeUndefinedDeep(payload));
  return ref.id;
}

export async function updateHandbookTrainingNode(
  nodeId: string,
  input: SaveHandbookTrainingNodeInput,
): Promise<void> {
  const err = await validateHandbookTrainingSave(input, nodeId);
  if (err) throw new Error(err);

  const payload: Record<string, unknown> = {
    kind: input.kind,
    parentId: input.parentId,
    title: input.title.trim(),
    published: input.published === true,
    updatedByUserId: input.updatedByUserId,
    updatedAt: serverTimestamp(),
  };
  payload.description = input.description?.trim() || null;
  if (input.kind === 'video') {
    payload.loomUrl = input.loomUrl!.trim();
    payload.slug = normalizeHandbookTrainingSlug(input.slug || input.title);
    payload.recordedAt = input.recordedAt?.trim() || null;
    payload.durationSec = input.durationSec != null && input.durationSec > 0 ? input.durationSec : null;
    payload.covers = input.covers || null;
    payload.outline = input.outline?.length ? input.outline : null;
    payload.staleIf = input.staleIf?.length ? input.staleIf : null;
  } else {
    payload.loomUrl = null;
    payload.slug = null;
    payload.recordedAt = null;
    payload.durationSec = null;
    payload.covers = null;
    payload.outline = null;
    payload.staleIf = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore null deletes optional fields
  await updateDoc(doc(db, HANDBOOK_TRAINING_NODES, nodeId), payload as any);
}

export async function deleteHandbookTrainingNode(nodeId: string, allNodes: HandbookTrainingNode[]): Promise<void> {
  const hasChild = allNodes.some(n => n.parentId === nodeId);
  if (hasChild) throw new Error('Remove or move child items before deleting this folder.');
  await deleteDoc(doc(db, HANDBOOK_TRAINING_NODES, nodeId));
}

export async function moveHandbookTrainingNode(
  nodeId: string,
  newParentId: string | null,
  allNodes: HandbookTrainingNode[],
  updatedByUserId: string,
): Promise<void> {
  const node = allNodes.find(n => n.id === nodeId);
  if (!node) throw new Error('Item not found.');
  const err = assertValidHandbookTrainingMove(node, newParentId, allNodes);
  if (err) throw new Error(err);

  const oldParentId = node.parentId ?? null;
  const targetParent = newParentId ?? null;
  if (oldParentId === targetParent) return;

  const newSiblings = allNodes.filter(
    n => (n.parentId ?? null) === targetParent && n.id !== nodeId,
  );
  const sortOrder = newSiblings.length ? Math.max(...newSiblings.map(s => s.sortOrder)) + 1 : 0;

  const batch = writeBatch(db);
  batch.update(doc(db, HANDBOOK_TRAINING_NODES, nodeId), {
    parentId: targetParent,
    sortOrder,
    updatedByUserId,
    updatedAt: serverTimestamp(),
  });

  const oldSiblings = allNodes
    .filter(n => (n.parentId ?? null) === oldParentId && n.id !== nodeId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  oldSiblings.forEach((s, index) => {
    batch.update(doc(db, HANDBOOK_TRAINING_NODES, s.id), {
      sortOrder: index,
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
}

export async function reorderHandbookTrainingSiblings(
  parentId: string | null,
  orderedIds: string[],
): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    batch.update(doc(db, HANDBOOK_TRAINING_NODES, id), {
      sortOrder: index,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
  void parentId;
}

/** Reorder among siblings (optionally reparenting dragged node to target's parent). */
export async function insertHandbookTrainingNodeAmongSiblings(
  draggedId: string,
  targetId: string,
  insertBefore: boolean,
  allNodes: HandbookTrainingNode[],
  updatedByUserId: string,
): Promise<void> {
  const plan = computeSiblingOrderAfterInsert(allNodes, draggedId, targetId, insertBefore);
  if (!plan) throw new Error('Could not reorder this item.');

  const dragged = allNodes.find(n => n.id === draggedId);
  if (!dragged) throw new Error('Item not found.');

  const oldParentId = dragged.parentId ?? null;
  const parentChanged = oldParentId !== plan.parentId;
  if (parentChanged) {
    const err = assertValidHandbookTrainingMove(dragged, plan.parentId, allNodes);
    if (err) throw new Error(err);
  }

  const batch = writeBatch(db);
  plan.orderedIds.forEach((id, index) => {
    const patch: {
      sortOrder: number;
      updatedAt: ReturnType<typeof serverTimestamp>;
      parentId?: string | null;
      updatedByUserId?: string;
    } = {
      sortOrder: index,
      updatedAt: serverTimestamp(),
    };
    if (id === draggedId && parentChanged) {
      patch.parentId = plan.parentId;
      patch.updatedByUserId = updatedByUserId;
    }
    batch.update(doc(db, HANDBOOK_TRAINING_NODES, id), patch);
  });

  if (parentChanged) {
    const oldSiblings = allNodes
      .filter(n => (n.parentId ?? null) === oldParentId && n.id !== draggedId)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
    oldSiblings.forEach((s, index) => {
      batch.update(doc(db, HANDBOOK_TRAINING_NODES, s.id), {
        sortOrder: index,
        updatedAt: serverTimestamp(),
      });
    });
  }

  await batch.commit();
}

export async function updateFreshnessReportStatus(
  reportId: string,
  status: HandbookTrainingFreshnessStatus,
): Promise<void> {
  await updateDoc(doc(db, HANDBOOK_TRAINING_FRESHNESS, reportId), {
    status,
    updatedAt: serverTimestamp(),
  });
}

export function nextSortOrderAmongSiblings(nodes: HandbookTrainingNode[], parentId: string | null): number {
  const siblings = nodes.filter(n => (n.parentId || null) === parentId);
  if (!siblings.length) return 0;
  return Math.max(...siblings.map(s => s.sortOrder)) + 1;
}
